/**
 * Admin → Edge Fleet
 *
 * Phase 7 item 1 (2026-05-16). Per-tenant fleet view across all three
 * Edge components (Discovery / Activity / Detection). Each pod row
 * shows heartbeat freshness, lag watermark, drop counters, version pin,
 * and current health tri-state (healthy/stale/down) derived server-side
 * from the staleness sweep.
 *
 * Read-only for this batch. Provisioning wizard lives at
 * /admin/edge-provisioning; per-pod detail drawer + scale knobs land in
 * Phase 7 batch B.
 *
 * Backend:
 *   GET /v1/edge/fleet → { pods: [...], summary: {...} }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  RefreshCw,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Server,
  Plus,
  Cpu,
  Activity as ActivityIcon,
  ShieldAlert,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listEdgeFleet,
  type EdgePodSummary,
  type EdgeFleetSummary,
  type EdgeComponent,
  type EdgeHealthStatus,
  getAgentEntitlement,
  type AgentEntitlementVerdict,
} from '@/lib/api';
import { EdgePodDetailDrawer } from './EdgePodDetailDrawer';
import { BudgetWatchdogPanel } from './BudgetWatchdogPanel';
import { InstallTokenPanel } from './InstallTokenPanel';

const COMPONENT_LABELS: Record<EdgeComponent, string> = {
  discovery: 'Discovery',
  activity: 'Activity',
  detection: 'Detection',
};

const COMPONENT_ICON: Record<EdgeComponent, typeof Server> = {
  discovery: Server,
  activity: ActivityIcon,
  detection: ShieldAlert,
};

type ComponentFilter = EdgeComponent | 'all';
type HealthFilter = EdgeHealthStatus | 'all';

const HEALTH_VARIANT: Record<EdgeHealthStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  healthy: 'success',
  degraded: 'warning',
  stale: 'warning',
  down: 'critical',
  decommissioned: 'glass',
};

const HEALTH_ICON: Record<EdgeHealthStatus, typeof CheckCircle2> = {
  pending: Clock,
  healthy: CheckCircle2,
  degraded: AlertTriangle,
  stale: Clock,
  down: XCircle,
  decommissioned: XCircle,
};

/**
 * Render agent self-measurement (Command Plane Phase 1).
 *
 * THE RULE THESE ENCODE: null means NOT REPORTED and must never render as 0.
 * Until 2026-08-11 no agent measured itself at all, and every agent class
 * except the scanner still reports nothing - so most rows legitimately have no
 * reading. A 0% in those rows would tell an operator that an un-instrumented
 * agent is idle, which is precisely the false-confidence the command-centre
 * design doc warns about ("the honest console label is 'last reported', never
 * 'protected'").
 *
 * An em dash is deliberately not a zero and not a blank: blank reads as a
 * rendering bug, zero reads as a measurement.
 */
const NOT_REPORTED = '—';

/**
 * Prefer the instantaneous rate when the agent could honestly observe one;
 * otherwise fall back to cumulative CPU seconds, which is meaningful even for
 * the one-shot scanner. Only if BOTH are absent do we say "not reported".
 */
export function formatCpu(p: { cpuPercent: number | null; cpuSecondsTotal: number | null }): string {
  if (p.cpuPercent != null) return `${p.cpuPercent.toFixed(1)}%`;
  if (p.cpuSecondsTotal != null) return `${p.cpuSecondsTotal.toFixed(1)}s`;
  return NOT_REPORTED;
}

/** Explains which of the two numbers the cell is showing, and why. */
export function cpuTitle(p: {
  cpuPercent: number | null;
  cpuSecondsTotal: number | null;
  measuredAt: string | null;
}): string {
  if (p.cpuPercent == null && p.cpuSecondsTotal == null) {
    return 'No measurement reported by this agent.';
  }
  const when = p.measuredAt ? ` (sampled ${new Date(p.measuredAt).toLocaleString()})` : '';
  return p.cpuPercent != null
    ? `Instantaneous CPU, may exceed 100% on multicore${when}`
    : `Cumulative CPU seconds - this agent runs one-shot, so no rate could be observed${when}`;
}

export function formatRss(rssMb: number | null): string {
  if (rssMb == null) return NOT_REPORTED;
  return rssMb >= 1024 ? `${(rssMb / 1024).toFixed(1)} GiB` : `${rssMb} MiB`;
}

function heartbeatAge(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function AdminEdgeFleet(): JSX.Element {
  const navigate = useNavigate();
  const [pods, setPods] = useState<EdgePodSummary[]>([]);
  // Endpoint class only in the banner: it is the line customers actually
  // negotiate and the one a 20,000-laptop rollout breaches. Pods and gateways
  // are visible in the detail view rather than competing for the banner.
  const [entitlement, setEntitlement] = useState<AgentEntitlementVerdict | null>(null);
  const [summary, setSummary] = useState<EdgeFleetSummary>({
    total: 0, healthy: 0, degraded: 0, stale: 0, down: 0, pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [componentFilter, setComponentFilter] = useState<ComponentFilter>('all');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('all');
  const [drawerPodId, setDrawerPodId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listEdgeFleet({
        ...(componentFilter !== 'all' ? { component: componentFilter } : {}),
        ...(healthFilter !== 'all' ? { healthStatus: healthFilter } : {}),
      });
      setPods(r.pods);
      setSummary(r.summary);
      // Best-effort: an entitlement lookup that fails must not blank the fleet
      // view, which is the page an operator opens during an incident.
      void getAgentEntitlement()
        .then((e) => setEntitlement(e.classes.endpoint))
        .catch(() => setEntitlement(null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load fleet');
    } finally {
      setLoading(false);
    }
  }, [componentFilter, healthFilter]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  const byComponent = useMemo(() => {
    const m: Record<EdgeComponent, number> = { discovery: 0, activity: 0, detection: 0 };
    for (const p of pods) m[p.component] += 1;
    return m;
  }, [pods]);

  return (
    <PageContainer>
      <PageHeader
        title="Edge Fleet"
        subtitle="Live view across all Edge components. Heartbeats refresh on a 30s cadence; staleness tri-state is computed server-side from the heartbeat timestamp."
        badges={[
          { label: 'Live · 30s poll', variant: 'glass' },
          { label: 'CISO health', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => navigate({ to: '/admin/edge-provisioning' })}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Provision pod
            </Button>
          </div>
        }
      />

      {error && (
        <ErrorState
          title="Failed to load fleet"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      {/*
        Entitlement banner. Shown ONLY when there is something to say - a
        permanent "you are within your limit" strip is noise that trains an
        operator to stop reading this area, which is the one place the real
        warning has to land.

        Note what it never says: nothing here suggests agents are blocked,
        because they are not. Enrolment always continues; only new install
        tokens are gated. Saying otherwise would send an operator hunting for
        an outage that does not exist.
      */}
      {entitlement && entitlement.state !== 'within' && entitlement.state !== 'unlimited' && (
        <div
          className={`mb-6 rounded-lg border p-4 flex items-start gap-3 ${
            entitlement.state === 'over'
              ? 'border-red-500/40 bg-red-500/10'
              : 'border-amber-500/40 bg-amber-500/10'
          }`}
        >
          <AlertTriangle
            className={`h-5 w-5 mt-0.5 shrink-0 ${
              entitlement.state === 'over' ? 'text-red-400' : 'text-amber-400'
            }`}
          />
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {entitlement.state === 'over'
                ? `${entitlement.over} endpoint agent(s) over entitlement`
                : `${entitlement.over} endpoint agent(s) over - inside the grace band`}
            </div>
            <p className="text-xs text-muted-foreground">{entitlement.summary}</p>
            <p className="text-[11px] text-muted-foreground">
              Your agents keep running and new devices still enrol. The agent ceiling is managed by
              FOCTTA - request an increase to issue new install tokens.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Pods" value={summary.total} icon={Server} hint={`${byComponent.discovery}/${byComponent.activity}/${byComponent.detection} disc/act/det`} />
        <KpiCard label="Healthy" value={summary.healthy} icon={CheckCircle2} glow={summary.healthy > 0 ? 'high' : 'low'} hint="heartbeat fresh" />
        <KpiCard label="Stale / Down" value={summary.stale + summary.down} icon={AlertTriangle} glow={summary.down > 0 ? 'critical' : summary.stale > 0 ? 'medium' : 'low'} hint={`${summary.down} down · ${summary.stale} stale`} />
        <KpiCard label="Pending" value={summary.pending} icon={Clock} glow={summary.pending > 0 ? 'medium' : 'low'} hint="awaiting first heartbeat" />
      </div>

      <GlassCard className="p-6">
        <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Fleet</CardTitle>
            <CardDescription>
              Click a row to open the per-pod detail drawer (scale knobs + diagnose ship in Batch B).
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={componentFilter}
              onChange={(e) => setComponentFilter(e.target.value as ComponentFilter)}
              className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              <option value="all">All components</option>
              <option value="discovery">Discovery</option>
              <option value="activity">Activity</option>
              <option value="detection">Detection</option>
            </select>
            <select
              value={healthFilter}
              onChange={(e) => setHealthFilter(e.target.value as HealthFilter)}
              className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              <option value="all">All health</option>
              <option value="healthy">Healthy</option>
              <option value="degraded">Degraded</option>
              <option value="stale">Stale</option>
              <option value="down">Down</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </CardHeader>

        {loading && <LoadingState caption="Loading fleet…" />}

        {!loading && pods.length === 0 && (
          <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
            <Server className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No Edge pods registered</div>
            <div className="mt-1">
              Use the <strong>Provision pod</strong> action (top-right) to mint an install bundle.
              Once the pod heartbeats in, it will appear here within 30s.
            </div>
          </div>
        )}

        {!loading && pods.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/30">
                  <th className="py-2 pr-3 font-normal">Pod</th>
                  <th className="py-2 pr-3 font-normal">Component</th>
                  <th className="py-2 pr-3 font-normal">Health</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Last heartbeat</th>
                  <th className="py-2 pr-3 font-normal">Version</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">CPU</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">RSS</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Lag</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Events 24h</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Drops 24h</th>
                  <th className="py-2 pr-3 font-normal">Cluster / NS</th>
                </tr>
              </thead>
              <tbody>
                {pods.map((p, i) => {
                  const HealthIcon = HEALTH_ICON[p.healthStatus];
                  const Component = COMPONENT_ICON[p.component];
                  return (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.01, 0.3) }}
                      className="border-b border-border/15 hover:bg-muted/20 cursor-pointer align-top"
                      onClick={() => setDrawerPodId(p.id)}
                      title={`Open pod ${p.podId}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="text-sm font-medium">{p.displayName}</div>
                        <code className="font-mono text-[10px] text-muted-foreground">{p.podId.slice(0, 16)}…</code>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge variant="glass" className="text-[10px] inline-flex items-center gap-1">
                          <Component className="h-3 w-3" />
                          {COMPONENT_LABELS[p.component]}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge variant={HEALTH_VARIANT[p.healthStatus]} className="text-[10px] inline-flex items-center gap-1">
                          <HealthIcon className="h-3 w-3" />
                          {p.healthStatus}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground" title={p.lastHeartbeatAt ?? ''}>
                        {heartbeatAge(p.lastHeartbeatAt)}
                      </td>
                      <td className="py-2 pr-3 font-mono text-[11px]">{p.version}</td>
                      {/*
                        Self-measurement (Phase 1). formatCpu / formatRss render
                        NOT REPORTED as an em dash rather than 0, so an agent
                        class that cannot measure itself reads as a gap. Showing
                        0% for an agent pinning a core is worse than showing
                        nothing - the reason these are nullable end to end.
                      */}
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px]" title={cpuTitle(p)}>
                        {formatCpu(p)}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                        {formatRss(p.rssMb)}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                        {p.lagWatermarkSeconds == null ? '-' : `${p.lagWatermarkSeconds}s`}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {p.eventsProcessed24h.toLocaleString('en-IN')}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <span className={p.eventsDropped24h > 0 ? 'text-sev-critical' : 'text-muted-foreground'}>
                          {p.eventsDropped24h.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                        {p.clusterName ?? '-'}
                        {p.namespace ? ` / ${p.namespace}` : ''}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/*
        Phase 6. Placed under the fleet table on purpose: doc 34 §4 orders the
        console by what a customer asks, and "which agent is misbehaving" comes
        after "what do I have" but before anything else.
      */}
      <div className="mt-4">
        <BudgetWatchdogPanel />
        <InstallTokenPanel />
      </div>

      <EdgePodDetailDrawer
        podId={drawerPodId}
        onClose={() => setDrawerPodId(null)}
        onMutated={() => void refresh()}
      />
    </PageContainer>
  );
}
